/**
 * @fileoverview Report Formatters for PSP/CRE Tool v2.0 - Tab 5 Reports
 * 
 * PURPOSE:
 * Transform raw calculation results from casualtyCalculator.js into formatted,
 * report-ready data structures for each of the 12 NATO-compliant reports.
 * 
 * ARCHITECTURE:
 * - Input: scenario object with calculation_results from casualtyCalculator.js
 * - Output: Formatted data objects ready for export to DOCX/PDF/Excel/HTML
 * - One formatter function per report type
 * - Pure functions - no side effects, fully testable
 * 
 * ALL 12 REPORTS:
 * 1. MEDSITREP - Daily Medical Situation Report (STANAG 2348)
 * 2. CASUALTY_ESTIMATE - Detailed Casualty Projections
 * 3. COMPONENTS_MATRIX - Traffic Light Status Dashboard
 * 4. MEDASSESSREP - Weekly Medical Assessment
 * 5. EPINATO2 - Disease/Injury Surveillance (STANAG 2535 / AMedP-4.1)
 * 6. NINE_LINE - 9-LINE MEDEVAC Request (STANAG 2087 / AAMedP-1.5)
 * 7. METHANE - Major Incident Report
 * 8. OOSR - Out of Standards Report
 * 9. BLUE_LIGHT - Surge Capacity Recommendations
 * 10. MEDEVAC_SUMMARY - Evacuation Asset Status
 * 11. EXECUTIVE_BRIEF - One-Page BLUF for Leadership
 * 12. NATO_PLANNING - Annex Q Medical Estimate (AJMedP-5)
 * 
 * @version 2.3 - BUG-111 + BUG-112 (March 14, 2026)
 *   BUG-111: ARCH-010 compliance - extractCASEVACData() removed entirely.
 *   All 12 formatters purged of CASEVAC DOW fields (dow_multiplier, additional_dow,
 *   casevac_percent, annex_q_casevac). Replaced with informational note where relevant.
 *   Doctrinal basis: CASEVAC cannot be planned per AJMedP-2. Tab5 ARCH-010 already
 *   correct - formatters were pre-ARCH-010 and never updated.
 *   BUG-112: Arrow/symbol non-ASCII replaced with ASCII equivalents.
 *   Prohibited phrase removed from @author.
 * @version 2.2 - Blue Light Protocol Tab3 Integration (February 13, 2026)
 * @author Dr. Tatar Radu MD
 * 
 * CHANGELOG:
 * v2.2 (February 13, 2026):
 * - CRITICAL FIX: formatBlueLightProtocol now reads Tab3 mascal_config decisions
 * - Added support for blue_light_recommended, blue_light_active, blue_light_dismissed
 * - Report now shows medical assessment vs command decision
 * - Displays trigger_reason, activated_at, dismiss_reason from Tab3
 * - Changed from recalculating triggers to reading actual user decisions
 * - Status levels: NORMAL, RECOMMENDED, ACTIVE, DISMISSED
 * 
 * v2.1 (January 15, 2025):
 * - T5-012: Added CASEVAC section to Executive Brief
 * - T5-013: Added CASEVAC paragraph to NATO Planning (Annex Q)
 * - T5-014: Added CASEVAC row to MEDSITREP
 * - extractCASEVACData was present in v2.1; removed in v2.3 (ARCH-010/BUG-111)
 * - Enhanced DOW explanation with CASEVAC impact
 */

// STATUS_THRESHOLDS available from reportConstants if needed for custom threshold logic
// import { STATUS_THRESHOLDS } from './reportConstants.js';

// ============================================================================
// HELPER FUNCTIONS - Status Determination
// ============================================================================

/**
 * Determine traffic light status (GREEN/AMBER/RED) based on value and thresholds
 */
export function determineStatus(value, thresholds, higherIsBetter = true) {
  if (value === null || value === undefined || isNaN(value)) {
    return 'RED';
  }

  if (higherIsBetter) {
    if (thresholds?.GREEN?.min !== undefined && value >= thresholds.GREEN.min) return 'GREEN';
    if (thresholds?.AMBER?.min !== undefined && value >= thresholds.AMBER.min) return 'AMBER';
    return 'RED';
  } else {
    if (thresholds?.GREEN?.max !== undefined && value <= thresholds.GREEN.max) return 'GREEN';
    if (thresholds?.AMBER?.max !== undefined && value <= thresholds.AMBER.max) return 'AMBER';
    return 'RED';
  }
}

/**
 * Format number with specified decimal places and optional unit
 */
export function formatNumber(value, decimals = 0, unit = '') {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return value.toFixed(decimals) + unit;
}

/**
 * Calculate percentage with proper handling of zero denominator
 */
export function safePercentage(numerator, denominator, decimals = 1) {
  if (!denominator || denominator === 0) return 0;
  return parseFloat(((numerator / denominator) * 100).toFixed(decimals));
}

/**
 * Format date/time for NATO reports
 */
export function formatDateTime(date, includeTime = true) {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  if (isNaN(d.getTime())) return 'Invalid Date';
  
  const datePart = d.toISOString().split('T')[0];
  if (!includeTime) return datePart;
  
  const timePart = d.toISOString().split('T')[1].slice(0, 8);
  return `${datePart} ${timePart}Z`;
}

// ============================================================================
// 1. MEDSITREP FORMATTER - Daily Medical Situation Report
// ============================================================================

export function formatMEDSITREP(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const medConfig = scenario.medical_configuration || {};
  const corps = calc.by_corps || {};
  const medAnalysis = calc.medical_system_analysis || {};

  // BUG-RPT-001: v3 uses mtf_structure.role2/.role3; v2 used role2_basic/role2_enhanced/role3
  const mtfStr = medConfig.mtf_structure || {};
  const r2 = mtfStr.role2 || medConfig.role2_basic || medConfig.role2_enhanced || null;
  const r3 = mtfStr.role3 || medConfig.role3 || null;

  // BUG-T46 fix: read individual R2B facilities from medConfig.facilities (primary source)
  // and fall back to per_facility_evacuation_assets for location strings.
  const facilityList = medConfig.facilities || medConfig.staging_facilities || [];
  const r2bFacilities = facilityList.filter(f =>
    f.type === 'ROLE2_BASIC' || f.echelon === 'R2B' || f.facility_type === 'ROLE2_BASIC'
  );
  const perFacility = medConfig.per_facility_evacuation_assets || [];
  const r2bLocations = r2bFacilities.length > 0
    ? r2bFacilities.map(f => ({
        name: f.name || f.facility_name || f.designation || 'R2B',
        location: f.location || f.grid_reference || null,
        beds: f.beds || f.bed_capacity || f.capacity?.beds || null
      }))
    : perFacility
        .filter(f => f.facility_type === 'ROLE2_BASIC' && f.location)
        .map(f => ({ name: f.facility_name || '', location: f.location, beds: null }));
  const r3Location = perFacility.find(f => f.facility_type === 'ROLE3' && f.location)?.location
    || r3?.location
    || null;
  // For report display: join multiple R2B locations; fall back to TBD if none entered.
  // R2E locations are saved in per_facility_evacuation_assets and available for Tab 7 sketch map.
  const fmtLocations = (arr) => arr.length > 0
    ? arr.map(e => e.name ? `${e.name}: ${e.location}` : e.location).join(' | ')
    : null;
  const r2bLocationStr = fmtLocations(r2bLocations) || r2?.location || null;

  return {
    metadata: {
      report_type: 'MEDSITREP',
      dtg: formatDateTime(new Date()),
      reporting_unit: scenario.force?.designation || 'Unknown Unit',
      reporting_period: 'Last 24 hours',
      classification: 'NATO UNCLASSIFIED'
    },

    deployed_hospital_care: {
      // BUG-T46 fix: aggregate all R2B facilities from medConfig.facilities; show each individually.
      role2_basic: (r2bFacilities.length > 0 || (r2 && (r2.count > 0 || r2.beds > 0))) ? (() => {
        // Prefer actual per-facility beds; fall back to aggregate divided evenly
        const totalBeds = r2bFacilities.length > 0
          ? r2bFacilities.reduce((s, f) => s + (f.beds || f.bed_capacity || f.capacity?.beds || 0), 0)
          : (r2?.beds || 0);
        const facilityCount = r2bFacilities.length || r2?.count || 1;
        const bedsPerFacility = facilityCount > 0 ? Math.round(totalBeds / facilityCount) : totalBeds;
        const facilities = r2bLocations.length > 0
          ? r2bLocations.map(f => ({ name: f.name || 'R2B', location: f.location, beds: f.beds || bedsPerFacility }))
          : [{ name: 'R2B', location: r2?.location || null, beds: totalBeds }];
        return {
          location: r2bLocationStr || 'LOCATION TBD — configure in Tab 3',
          beds_total: totalBeds,
          facility_count: facilityCount,
          facilities,
          beds_occupied: Math.round(totalBeds * 0.6),
          beds_available: Math.round(totalBeds * 0.4),
          occupancy_rate: 60,
          status: 'GREEN',
          surgical_teams: r2.surgicalTeams || r2.surgical_teams || 0,
          surgical_status: (() => { const t = r2.surgicalTeams || r2.surgical_teams || 0; return t > 0 ? `${t} of ${t} available` : 'PENDING — configure in Tab 3'; })()
        };
      })() : null,

      role2_enhanced: null, // R2E bed data not tracked separately from R2B aggregate; location available via per_facility_evacuation_assets for Tab 7

      role3: (r3 && (r3.count > 0 || r3.beds > 0)) ? {
        location: r3Location || 'LOCATION TBD — configure in Tab 3',
        beds_total: r3.beds || 0,
        beds_occupied: Math.round((r3.beds || 0) * 0.5),
        beds_available: Math.round((r3.beds || 0) * 0.5),
        occupancy_rate: 50,
        status: 'GREEN',
        surgical_teams: r3.surgicalTeams || r3.surgical_teams || 0,
        surgical_status: (() => { const t = r3.surgicalTeams || r3.surgical_teams || 0; return t > 0 ? `${t} of ${t} available` : 'PENDING — configure in Tab 3'; })(),
        icu_beds: r3.icuBeds || r3.icu_beds || 0
      } : null
    },

    medevac_assets: (() => {
      // BUG-T53 fix: read actual configured MEDEVAC asset count; removed hardcoded fallback of 4
      // BUG-T47 fix: prefer medevacAnalysis (actual calc) over medConfig for all asset fields
      const heliCalc = medAnalysis.medevac?.helicopter || {};
      const aviationAssets = medConfig.aviation_assets || medConfig.medevac_assets || [];
      const configuredTotal = heliCalc.total_aircraft
        || (Array.isArray(aviationAssets) && aviationAssets.length > 0 ? aviationAssets.length : 0)
        || (medConfig.medevac?.helicopters || calc.total_medevac_helicopters || 0);
      const configuredAvail = heliCalc.available_aircraft
        || medConfig.medevac?.available
        || (configuredTotal > 0 ? Math.round(configuredTotal * 0.9) : 0);
      const effectiveAvailPct = heliCalc.effective_availability_percent
        || (configuredTotal > 0 ? Math.round(configuredAvail / configuredTotal * 100) : 0);
      // Prefer actual daily_sorties from calc; fall back to planning estimate (WIA/day ÷ 4 pts/sortie)
      const dailyMissions = heliCalc.daily_sorties
        || Math.round((corps.total_wia || 0) / (calc.duration_days || 1) / 4);
      return {
        total: configuredTotal,
        available: configuredAvail,
        availability_rate: effectiveAvailPct,
        status: configuredTotal > 0 ? 'GREEN' : 'RED',
        missions_last_24h: dailyMissions,
        // BUG-6K-002: avg_response_time is null when missions = 0
        avg_response_time: dailyMissions > 0 ? (medAnalysis.avg_response_time || null) : null,
        golden_hour_achievement: medAnalysis.overall_golden_hour_percent ?? null
      };
    })(),
    
    
    battle_casualties: {
      // BUG-T50 fix: battle = KIA+DOW+WIA only — DNBI are non-battle casualties (AJP-4.10C)
      total: (corps.total_kia || 0) + (corps.total_dow || 0) + (corps.total_wia || 0),
      kia: corps.total_kia || 0,
      dow: corps.total_dow || 0,
      wia: corps.total_wia || 0,
      // BUG-T49 fix: RTD is correct NATO abbreviation (Return To Duty), not RTN
      rtd: corps.total_rtd || 0,
      daily_rate: corps.avg_daily_rate || 0,
      by_unit: calc.by_brigade || []
    },

    dnbi: {
      total: corps.total_dnbi || 0,
      // BUG-T45 fix: divide by duration_days to get per-day rate; was missing /duration giving 41.0 instead of 4.1
      rate_per_1000: Math.round(
        safePercentage(corps.total_dnbi || 0, scenario.force?.total_personnel || 1) * 10
        / (calc.duration_days || 1) * 10
      ) / 10,
      top_categories: [
        { category: 'Respiratory', cases: Math.round((corps.total_dnbi || 0) * 0.25) },
        { category: 'Musculoskeletal', cases: Math.round((corps.total_dnbi || 0) * 0.20) },
        { category: 'Gastrointestinal', cases: Math.round((corps.total_dnbi || 0) * 0.15) }
      ],
      outbreak_alerts: []
    },
    
    health_risks: {
      environmental: [],
      disease_threats: [],
      preventive_measures: []
    },
    
    medlog: {
      medical_materiel_days: 7,
      medical_materiel_status: 'GREEN',
      blood_availability: 85,
      blood_status: 'GREEN',
      critical_shortages: []
    },
    
    evacuation: {
      // BUG-6K-003 FIX: 78 was a hardcoded fallback with no basis — show null when no calculation results
      golden_hour_achievement: medAnalysis.overall_golden_hour_percent ?? null,
      dow_rate: safePercentage(corps.total_dow || 0, corps.total_wia || 1),
      dow_status: 'GREEN',
      dow_trend: '->',
      // BUG-T47 fix: effective_availability from actual medevac calc, not hardcoded 75
      effective_availability: { percentage: medAnalysis.medevac?.helicopter?.effective_availability_percent ?? 75 },
      medevac_status: 'GREEN',
      casevac_note: 'CASEVAC informational only per AJMedP-2 - not a planning factor (ARCH-010).'
    }
  };
}

// ============================================================================
// 2. CASUALTY ESTIMATE FORMATTER - Detailed Casualty Projections
// ============================================================================

export function formatCasualtyEstimate(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const corps = calc.by_corps || {};
  const medReq = calc.medical_requirements || {};
  
  
  return {
    metadata: {
      report_type: 'CASUALTY_ESTIMATE',
      dtg: formatDateTime(new Date()),
      reporting_unit: scenario.force?.designation || 'Unknown Unit',
      reporting_period: `${calc.duration_days || 0} days`,
      classification: 'NATO UNCLASSIFIED',
      stanag: 'AJMedP-1'
    },
    
    executive_summary: {
      force_size: scenario.force?.total_personnel || calc.total_personnel || 0,
      operation_duration: calc.duration_days || 0,
      total_casualties: (corps.total_all_sources || corps.total_casualties || 0) + (corps.total_dnbi || 0),
      casualty_rate: corps.casualty_rate_percent || 0
    },
    
    battle_casualties: {
      total: corps.total_all_sources || corps.total_casualties || 0,
      kia: corps.total_kia || 0,
      dow: corps.total_dow || 0,
      wia: corps.total_wia || 0,
      mia: corps.total_mia || 0,
      daily_average: Math.round((corps.total_all_sources || corps.total_casualties || 0) / (calc.duration_days || 1)),
      by_day: calc.daily_totals || [],
      by_unit: calc.by_brigade || []
    },
    
    // Enhanced DOW section with CASEVAC explanation
    dow_analysis: {
      total_dow: corps.total_dow || 0,
      dow_rate: safePercentage(corps.total_dow || 0, corps.total_wia || 1),
      factors: {
        evacuation_time: 'Primary factor - time to surgical care',
        casevac_impact: 'CASEVAC not a planning factor per AJMedP-2 (ARCH-010). MEDEVAC overflow modelled as unevacuated.',
        blood_availability: 'Secondary factor - transfusion delays',
        surgical_queue: 'Secondary factor - OR availability'
      },
      casevac_contribution: null // ARCH-010: CASEVAC not modelled as DOW factor per AJMedP-2
    },
    
    dnbi: {
      total: corps.total_dnbi || 0,
      daily_average: Math.round((corps.total_dnbi || 0) / (calc.duration_days || 1)),
      categories: {
        musculoskeletal: Math.round((corps.total_dnbi || 0) * 0.50),
        mental_health: Math.round((corps.total_dnbi || 0) * 0.23),
        infectious: Math.round((corps.total_dnbi || 0) * 0.15),
        accidents: Math.round((corps.total_dnbi || 0) * 0.08),
        other: Math.round((corps.total_dnbi || 0) * 0.04)
      }
    },
    
    triage_distribution: {
      t1_immediate: Math.round((corps.total_wia || 0) * 0.15),
      t2_delayed: Math.round((corps.total_wia || 0) * 0.25),
      t3_minimal: Math.round((corps.total_wia || 0) * 0.55),
      t4_expectant: Math.round((corps.total_wia || 0) * 0.05)
    },
    
    medical_requirements: {
      blood: {
        prbc_units: medReq.blood?.prbc || Math.round((corps.total_wia || 0) * 3),
        ffp_units: medReq.blood?.ffp || Math.round((corps.total_wia || 0) * 2.5),
        platelets: medReq.blood?.plt || Math.round((corps.total_wia || 0) * 1),
        total_units: medReq.blood?.total_units || Math.round((corps.total_wia || 0) * 6.5)
      },
      surgical: {
        total_hours: medReq.surgical?.total_hours || Math.round((corps.total_wia || 0) * 2.5),
        daily_average: Math.round((medReq.surgical?.total_hours || 0) / (calc.duration_days || 1))
      }
    },
    
    rtd_projection: {
      total_rtd: corps.total_rtd || 0,
      rtd_rate: safePercentage(corps.total_rtd || 0, corps.total_all_sources || 1),
      by_category: {
        wia_rtd: Math.round((corps.total_wia || 0) * 0.6),
        dnbi_rtd: Math.round((corps.total_dnbi || 0) * 0.85)
      }
    }
  };
}

// ============================================================================
// 3. COMPONENTS MATRIX FORMATTER - Traffic Light Dashboard
// ============================================================================

export function formatComponentsMatrix(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const medAnalysis = calc.medical_system_analysis || {};
  
  
  // BUG-6K-003 FIX: null when no calculation results; 78 was hardcoded with no basis
  const goldenHour = medAnalysis.overall_golden_hour_percent ?? null;
  const bedUtil = medAnalysis.bed_utilization_percent || 65;
  
  return {
    metadata: {
      report_type: 'COMPONENTS_MATRIX',
      dtg: formatDateTime(new Date()),
      reporting_unit: scenario.force?.designation || 'Unknown Unit',
      classification: 'NATO UNCLASSIFIED'
    },
    
    components: [
      {
        name: 'Medical Treatment Facilities',
        status: bedUtil < 80 ? 'GREEN' : bedUtil < 95 ? 'AMBER' : 'RED',
        metric: `${bedUtil}% utilization`,
        assessment: bedUtil < 80 ? 'Adequate capacity' : bedUtil < 95 ? 'Monitor closely' : 'At capacity',
        trend: '->'
      },
      {
        name: 'MEDEVAC',
        status: goldenHour >= 80 ? 'GREEN' : goldenHour >= 60 ? 'AMBER' : 'RED',
        metric: `${goldenHour}% golden hour`,
        assessment: goldenHour >= 80 ? 'Meeting standards' : goldenHour >= 60 ? 'Below target' : 'Critical shortfall',
        trend: goldenHour >= 80 ? 'UP' : '->'
      },

      // INC-001 FIX: wire real data; fall back with "(estimated)" label when absent
      (() => {
        const finalDos = calc.medical_requirements?.resupply_summary?.dos_projection?.final_dos;
        const hasDos   = finalDos != null;
        const dos      = hasDos ? Math.round(finalDos * 10) / 10 : null;
        const status   = !hasDos ? 'AMBER' : dos >= 7 ? 'GREEN' : dos >= 3 ? 'AMBER' : 'RED';
        const metric   = hasDos ? `${dos} DOS` : 'N/A (not configured)';
        const assessment = !hasDos
          ? 'Resupply not configured — configure in Tab 4'
          : dos >= 7 ? 'Adequate stock' : dos >= 3 ? 'Low stock — monitor' : 'Critical — imminent stockout';
        return { name: 'Medical Materiel', status, metric, assessment, trend: '->' };
      })(),
      (() => {
        const bloodRequired = calc.medical_requirements?.blood?.total_units || 0;
        const initStock     = scenario?.logistics?.medical_supplies?.initial_stock || {};
        const initTotal     = (initStock.prbc_units || 0) + (initStock.ffp_units || 0)
                            + (initStock.platelets_units || 0) + (initStock.cryo_units || 0);
        const resupplyUnits = calc.medical_requirements?.resupply_summary?.total_units || {};
        const resupplyTotal = (resupplyUnits.prbc || 0) + (resupplyUnits.ffp || 0)
                            + (resupplyUnits.plt  || 0) + (resupplyUnits.cryo || 0);
        const totalAvail    = initTotal + resupplyTotal;
        const hasCoverage   = bloodRequired > 0 && (initTotal > 0 || resupplyTotal > 0);
        const coverage      = hasCoverage ? Math.min(100, Math.round(totalAvail / bloodRequired * 100)) : null;
        const status        = coverage == null ? 'AMBER' : coverage >= 80 ? 'GREEN' : coverage >= 50 ? 'AMBER' : 'RED';
        const metric        = coverage != null ? `${coverage}% coverage` : 'N/A (no stock data)';
        const assessment    = coverage == null
          ? 'Enter initial stock in Tab 4 to assess'
          : coverage >= 80 ? 'Stock adequate' : coverage >= 50 ? 'Partial stock — plan resupply' : 'Critical shortage';
        return { name: 'Blood Products', status, metric, assessment, trend: '->' };
      })(),
      {
        // INC-001: medical_personnel_fill_rate is not a calculator output.
        // Shown as "Not assessed" until a personnel module is implemented.
        name: 'Medical Personnel',
        status: 'AMBER',
        metric: 'Not assessed',
        assessment: 'Personnel fill rate not available — assess manually',
        trend: '->'
      },
      {
        name: 'Patient Movement',
        status: goldenHour >= 80 ? 'GREEN' : 'AMBER',
        metric: `${goldenHour}% within 60 min`,
        assessment: goldenHour >= 80 ? 'On target' : 'Below target',
        trend: '->'
      }
    ],
    
    overall_status: {
      status: goldenHour >= 80 && bedUtil < 80 ? 'GREEN' : 'AMBER',
      summary: goldenHour >= 80 && bedUtil < 80 
        ? 'Medical support adequate for current operations'
        : 'Medical support requires monitoring',
      key_risks: [
        ...(goldenHour < 80 ? ['MEDEVAC capacity below target'] : []),
        ...(bedUtil >= 80 ? ['MTF capacity approaching limits'] : []),
      ]
    },

  };
}

// ============================================================================
// 4. MEDASSESSREP FORMATTER - Weekly Medical Assessment
// ============================================================================

export function formatMEDASSESSREP(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const corps = calc.by_corps || {};
  const medAnalysis = calc.medical_system_analysis || {};
  const force = scenario.force || {};
  const duration = calc.duration_days || 1;

  // BUG-6K-003 FIX: null when no calculation results; 78 was hardcoded with no basis
  const goldenHour = medAnalysis.overall_golden_hour_percent ?? null;

  const kia = corps.total_kia || 0;
  const dow = corps.total_dow || 0;
  const wia = corps.total_wia || 0;
  const strength = force.total_personnel || 1;

  // BUG-T56 fix: DOW Rate = DOW / (KIA+WIA+DOW) * 100; was 0% because evacuation object was missing
  const battleTotal = kia + dow + wia;
  const dowRate = battleTotal > 0 ? Math.round(dow / battleTotal * 1000) / 10 : 0;

  // BUG-T57 fix: actual battle rate vs NATO planning norm (15 cas/1000/day) drives status
  const actualBattleRate = battleTotal / strength * 1000 / duration;
  const natoNorm = 15;
  const casualtyStatus = actualBattleRate > natoNorm * 1.5 ? 'RED'
    : actualBattleRate > natoNorm * 1.2 ? 'AMBER' : 'GREEN';
  const overallStatus = casualtyStatus === 'RED' ? 'RED'
    : (goldenHour !== null && goldenHour < 60) ? 'RED'
    : (casualtyStatus === 'AMBER' || (goldenHour !== null && goldenHour < 80)) ? 'AMBER'
    : 'GREEN';

  return {
    metadata: {
      report_type: 'MEDASSESSREP',
      dtg: formatDateTime(new Date()),
      reporting_unit: force.designation || 'Unknown Unit',
      // BUG-T54 fix: consistent reporting period label
      reporting_period: 'Last 24 hours (exercise)',
      classification: 'NATO UNCLASSIFIED'
    },

    executive_summary: {
      force_size: strength,
      // BUG-T50 fix applied here too: total_casualties = battle + DNBI displayed separately
      battle_casualties: battleTotal,
      dnbi_casualties: corps.total_dnbi || 0,
      overall_status: overallStatus
    },

    battle_casualties: {
      // BUG-T50 fix: battle = KIA+DOW+WIA only
      total: battleTotal,
      kia,
      dow,
      wia,
      daily_rate: corps.avg_daily_rate || Math.round(battleTotal / duration),
      status: casualtyStatus
    },

    dnbi: {
      total: corps.total_dnbi || 0,
      // BUG-T45 fix: /duration_days for per-day rate
      rate_per_1000: Math.round(
        safePercentage(corps.total_dnbi || 0, strength) * 10 / duration * 10
      ) / 10
    },

    // BUG-T56 fix: evacuation block now populated so dow_rate renders correctly
    evacuation: {
      dow_rate: dowRate,
      dow_status: dowRate > 20 ? 'RED' : dowRate > 15 ? 'AMBER' : 'GREEN',
      dow_trend: '->',
      golden_hour_achievement: goldenHour,
      effective_availability: { percentage: goldenHour ?? 0 }
    },

    components: {
      facilities: { status: 'GREEN', assessment: 'Adequate capacity' },
      medevac: {
        status: goldenHour === null ? 'GREEN' : goldenHour >= 80 ? 'GREEN' : goldenHour >= 60 ? 'AMBER' : 'RED',
        assessment: goldenHour !== null ? `${goldenHour}% golden hour achievement` : 'No calculation data'
      },
      medlog: { status: 'GREEN', assessment: '7 DOS' },
      personnel: { status: 'GREEN', assessment: '95% fill' },
      overall: { status: overallStatus, assessment: overallStatus === 'GREEN' ? 'Ready' : 'Monitor' }
    },

    risks: [
      ...(goldenHour !== null && goldenHour < 80 ? [{ risk: 'MEDEVAC capacity', mitigation: 'Request additional assets' }] : []),
      ...(casualtyStatus !== 'GREEN' ? [{ risk: 'Casualty rate exceeds planning norm', mitigation: 'Review fire support and CASEVAC posture' }] : [])
    ],

    recommendations: [
      ...(goldenHour !== null && goldenHour < 80 ? ['Request additional MEDEVAC helicopters'] : []),
      'Continue monitoring casualty trends'
    ]
  };
}

// ============================================================================
// 5. EPINATO2 FORMATTER - Disease/Injury Surveillance (STANAG 2535 / AMedP-4.1)
// QM-6I-001: STANAG 2558 = AMedP-8.17 (Medical Oxygen Standards) — wrong document.
//            STANAG 2535 = AMedP-4.1 (Deployment Health Surveillance) governs EpiNATO-2.
// ============================================================================

export function formatEPINATO2(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const corps = calc.by_corps || {};
  
  const totalDNBI = corps.total_dnbi || 0;
  
  return {
    metadata: {
      report_type: 'EPINATO2',
      dtg: formatDateTime(new Date()),
      reporting_unit: scenario.force?.designation || 'Unknown Unit',
      reporting_period: 'Weekly',
      classification: 'NATO UNCLASSIFIED',
      stanag: 'STANAG 2535'  // QM-6I-001: AMedP-4.1 Deployment Health Surveillance (governs EpiNATO-2)
    },

    summary: {
      total_cases: totalDNBI,
      // BUG-T55 fix: /duration_days for per-day rate; also ensures DNBI reads from corps.total_dnbi
      rate_per_1000: Math.round(
        safePercentage(totalDNBI, scenario.force?.total_personnel || 1) * 10
        / (calc.duration_days || 1) * 10
      ) / 10,
      trend: '->',
      alert_level: totalDNBI > 100 ? 'ELEVATED' : 'NORMAL'
    },
    
    categories: [
      { 
        name: 'Musculoskeletal', 
        icd10: 'M00-M99',
        cases: Math.round(totalDNBI * 0.50),
        rate: safePercentage(Math.round(totalDNBI * 0.50), scenario.force?.total_personnel || 1) * 10,
        trend: '->'
      },
      { 
        name: 'Mental/Behavioral', 
        icd10: 'F00-F99',
        cases: Math.round(totalDNBI * 0.23),
        rate: safePercentage(Math.round(totalDNBI * 0.23), scenario.force?.total_personnel || 1) * 10,
        trend: 'UP'
      },
      { 
        name: 'Respiratory', 
        icd10: 'J00-J99',
        cases: Math.round(totalDNBI * 0.15),
        rate: safePercentage(Math.round(totalDNBI * 0.15), scenario.force?.total_personnel || 1) * 10,
        trend: '->'
      },
      { 
        name: 'Injury/Accident', 
        icd10: 'S00-T98',
        cases: Math.round(totalDNBI * 0.08),
        rate: safePercentage(Math.round(totalDNBI * 0.08), scenario.force?.total_personnel || 1) * 10,
        trend: '->'
      },
      { 
        name: 'Other', 
        icd10: 'Various',
        cases: Math.round(totalDNBI * 0.04),
        rate: safePercentage(Math.round(totalDNBI * 0.04), scenario.force?.total_personnel || 1) * 10,
        trend: '->'
      }
    ],
    
    outbreak_alerts: [],
    
    environmental_factors: [
      { factor: 'Water quality', status: 'SATISFACTORY' },
      { factor: 'Food safety', status: 'SATISFACTORY' },
      { factor: 'Vector control', status: 'MONITORING' }
    ],
    
    recommendations: [
      'Continue mental health screening',
      'Enforce injury prevention measures',
      'Monitor respiratory illness trends'
    ]
  };
}

// ============================================================================
// 6. 9-LINE MEDEVAC FORMATTER (STANAG 2087 / AAMedP-1.5)
// QM-6I-001: STANAG 3204 = AAMedP-1.1 (Aeromedical Evacuation — broad doctrine).
//            STANAG 2087 = AAMedP-1.5 (Forward Aeromedical Evacuation) defines the 9-line request format.
// ============================================================================

export function formatNineLine(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const corps = calc.by_corps || {};
  
  return {
    metadata: {
      report_type: 'NINE_LINE',
      dtg: formatDateTime(new Date()),
      classification: 'NATO UNCLASSIFIED',
      stanag: 'STANAG 2087'  // QM-6I-001: AAMedP-1.5 Forward Aeromedical Evacuation (9-line format)
    },

    nine_line: {
      line1_location: 'GRID TBD',
      line2_frequency: 'FM 30.00',
      line3_patients: {
        a_urgent: Math.round((corps.total_wia || 0) / (calc.duration_days || 1) * 0.15),
        b_priority: Math.round((corps.total_wia || 0) / (calc.duration_days || 1) * 0.25),
        c_routine: Math.round((corps.total_wia || 0) / (calc.duration_days || 1) * 0.55),
        d_convenience: 0
      },
      line4_special_equipment: 'N - None required',
      line5_patients_by_type: {
        l_litter: Math.round((corps.total_wia || 0) / (calc.duration_days || 1) * 0.4),
        a_ambulatory: Math.round((corps.total_wia || 0) / (calc.duration_days || 1) * 0.6)
      },
      line6_security: 'N - No enemy troops',
      line7_marking: 'A - Panels',
      line8_nationality: 'A - Allied military',
      line9_cbrn: 'N - None'
    },
    
    additional_info: {
      terrain: 'Open',
      weather: 'VMC',
      threats: 'None reported',
      recommended_approach: 'From South'
    }
  };
}

// ============================================================================
// 7. METHANE FORMATTER - Major Incident Report
// ============================================================================

export function formatMETHANE(scenario) {
  // BUG-T61 fix: return string, not object — Blob([object]) serialises as "[object Object]"
  const sc = scenario?.scenario ?? scenario;
  const calc = sc?.calculation_results || sc?.casualty_projections || {};
  const corps = calc.by_corps || {};
  const duration = calc.duration_days || 1;

  const dailyWIA  = Math.round((corps.total_wia || 0) / duration);
  const dailyKIA  = Math.round((corps.total_kia || 0) / duration);
  const t1        = Math.round(dailyWIA * 0.15);
  const t2        = Math.round(dailyWIA * 0.25);
  const t3        = Math.round(dailyWIA * 0.55);
  const dtg       = formatDateTime(new Date());

  const lines = [
    'METHANE REPORT',
    'Classification: NATO UNCLASSIFIED',
    `DTG: ${dtg}`,
    '',
    `M - MAJOR INCIDENT: ${dailyWIA > 50 ? 'YES - Mass Casualty Event' : 'NO - Standard Operations'}`,
    'E - EXACT LOCATION: Forward Operating Area',
    'T - TYPE: Combat Operations',
    'H - HAZARDS: Standard combat hazards',
    'A - ACCESS: Via MSR ALPHA',
    'N - NUMBER OF CASUALTIES:',
    `    Total WIA (daily avg): ${dailyWIA}`,
    `    T1 Immediate:          ${t1}`,
    `    T2 Delayed:            ${t2}`,
    `    T3 Minimal:            ${t3}`,
    `    KIA (daily avg):       ${dailyKIA}`,
    'E - EMERGENCY SERVICES: MEDEVAC on standby',
    '',
    'RESOURCE REQUIREMENTS:',
    `  Ambulances:      ${Math.ceil(dailyWIA / 4)}`,
    `  Helicopters:     ${Math.ceil(dailyWIA * 0.15 / 2)}`,
    `  Surgical Teams:  ${Math.ceil(dailyWIA * 0.40 / 4)}`,
    `  Blood Units:     ${dailyWIA * 3}`,
  ];
  return lines.join('\n');
}

// ============================================================================
// 8. OOSR FORMATTER - Out of Standards Report
// ============================================================================

export function formatOOSR(scenario) {
  // BUG-T61 fix: return string, not object — Blob([object]) serialises as "[object Object]"
  const sc = scenario?.scenario ?? scenario;
  const calc = sc?.calculation_results || sc?.casualty_projections || {};
  const medAnalysis = calc.medical_system_analysis || {};
  const dtg         = formatDateTime(new Date());
  const unit        = sc?.force?.designation || 'Unknown Unit';

  // BUG-6K-003 FIX: null when no calculation results; 78 was hardcoded with no basis
  const goldenHour = medAnalysis.overall_golden_hour_percent ?? null;
  const items = [];

  // Check golden hour standard
  if (goldenHour !== null && goldenHour < 80) {
    items.push({
      item: 'Golden Hour Achievement',
      standard: '>=80%',
      actual: `${goldenHour}%`,
      variance: `${80 - goldenHour}% below standard`,
      severity: goldenHour < 60 ? 'CRITICAL' : 'MAJOR',
      corrective_action: 'Request additional MEDEVAC assets',
      timeline: 'Immediate'
    });
  }

  const critical = items.filter(i => i.severity === 'CRITICAL').length;
  const major    = items.filter(i => i.severity === 'MAJOR').length;
  const minor    = items.filter(i => i.severity === 'MINOR').length;
  const overall  = items.length === 0 ? 'All medical standards met' : `${items.length} item(s) require attention`;

  const lines = [
    'OUT OF STANDARDS REPORT (OOSR)',
    'Classification: NATO UNCLASSIFIED',
    `DTG: ${dtg}`,
    `Reporting Unit: ${unit}`,
    '',
    'SUMMARY:',
    `  Total Items Out of Standard: ${items.length}`,
    `  Critical: ${critical}`,
    `  Major:    ${major}`,
    `  Minor:    ${minor}`,
    '',
    'ITEMS OUT OF STANDARD:',
  ];

  if (items.length === 0) {
    lines.push('  All medical standards met.');
  } else {
    items.forEach((it, idx) => {
      lines.push(`  ${idx + 1}. ${it.item}`);
      lines.push(`     Standard:          ${it.standard}`);
      lines.push(`     Actual:            ${it.actual}`);
      lines.push(`     Variance:          ${it.variance}`);
      lines.push(`     Severity:          ${it.severity}`);
      lines.push(`     Corrective Action: ${it.corrective_action}`);
      lines.push(`     Timeline:          ${it.timeline}`);
    });
  }

  lines.push('');
  lines.push(`OVERALL ASSESSMENT: ${overall}`);

  return lines.join('\n');
}

// ============================================================================
// 9. BLUE LIGHT PROTOCOL FORMATTER - Surge Capacity
// v2.0 - February 13, 2026 - Now reads Tab3 mascal_config decisions
// ============================================================================

export function formatBlueLightProtocol(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const corps = calc.by_corps || {};
  const medAnalysis = calc.medical_system_analysis || {};
  const medConfig = scenario.medical_configuration || {};
  
  // v2.0: READ TAB3 MASCAL_CONFIG DECISIONS
  const mascalConfig = medConfig.mascal_config || {};
  const blueLightRecommended = mascalConfig.blue_light_recommended || false;
  const blueLightActive = mascalConfig.blue_light_active || false;
  const blueLightTriggerReason = mascalConfig.blue_light_trigger_reason || null;
  const blueLightActivatedAt = mascalConfig.blue_light_activated_at || null;
  const blueLightDismissed = mascalConfig.blue_light_dismissed || false;
  const blueLightDismissReason = mascalConfig.blue_light_dismiss_reason || null;
  
  
  // Calculate current metrics for context
  // BUG-6K-003 FIX: null when no calculation results; 78 was hardcoded with no basis
  const goldenHour = medAnalysis.overall_golden_hour_percent ?? null;
  const dailyWIA = Math.round((corps.total_wia || 0) / (calc.duration_days || 1));
  const bedUtilization = medAnalysis.bed_utilization_percent || 65;
  const bloodDOS = medConfig.blood_config?.current_stock?.prbc_units 
    ? Math.round(medConfig.blood_config.current_stock.prbc_units / (dailyWIA * 0.5 || 1))
    : 7;
  
  // Determine status level based on ACTUAL activation (not recalculation)
  let statusLevel = 'NORMAL';
  if (blueLightActive) {
    statusLevel = 'ACTIVE';
  } else if (blueLightRecommended && !blueLightDismissed) {
    statusLevel = 'RECOMMENDED';
  } else if (blueLightDismissed) {
    statusLevel = 'DISMISSED';
  }
  
  // Build decision summary
  let decisionSummary = '';
  if (blueLightActive) {
    decisionSummary = `Blue Light Protocol ACTIVATED on ${blueLightActivatedAt ? new Date(blueLightActivatedAt).toLocaleString() : 'unknown date'}`;
  } else if (blueLightRecommended && !blueLightDismissed) {
    decisionSummary = 'Blue Light Protocol activation RECOMMENDED by medical staff - awaiting command decision';
  } else if (blueLightDismissed) {
    decisionSummary = 'Blue Light Protocol recommendation DISMISSED by command';
  } else {
    decisionSummary = 'Blue Light Protocol not required - current capacity adequate';
  }
  
  return {
    metadata: {
      report_type: 'BLUE_LIGHT',
      dtg: formatDateTime(new Date()),
      classification: 'NATO UNCLASSIFIED'
    },
    
    // v2.0: Status reflects ACTUAL Tab3 decisions
    status: {
      level: statusLevel,
      recommended: blueLightRecommended,
      active: blueLightActive,
      dismissed: blueLightDismissed,
      decision_summary: decisionSummary
    },
    
    // v2.0: Medical assessment from Tab3
    medical_assessment: {
      recommended: blueLightRecommended,
      trigger_reason: blueLightTriggerReason,
      assessment: blueLightRecommended 
        ? 'Medical staff assessment: Conditions warrant Blue Light Protocol activation'
        : 'Medical staff assessment: Current medical capacity adequate'
    },
    
    // v2.0: Command decision from Tab3
    command_decision: {
      activated: blueLightActive,
      activated_at: blueLightActivatedAt,
      dismissed: blueLightDismissed,
      dismiss_reason: blueLightDismissReason,
      decision: blueLightActive 
        ? 'APPROVED - Protocol activated'
        : blueLightDismissed
        ? `NOT APPROVED - ${blueLightDismissReason || 'Operational constraints prevent activation'}`
        : blueLightRecommended
        ? 'PENDING - Awaiting command decision'
        : 'N/A - Not recommended'
    },
    
    // Current metrics for situational awareness
    current_metrics: {
      golden_hour: goldenHour,
      daily_wia: dailyWIA,
      bed_utilization: bedUtilization,
      blood_dos: bloodDOS,
      casevac_note: 'CASEVAC informational only per AJMedP-2 (ARCH-010).'
    },
    
    // Surge capacity implementation (if active)
    surge_capacity: {
      status: blueLightActive ? 'IMPLEMENTED' : blueLightRecommended ? 'STANDBY' : 'NOT REQUIRED',
      additional_beds: blueLightActive ? 20 : 0,
      extended_shift: blueLightActive ? 'YES - 12 hour shifts' : 'NO',
      walking_blood_bank: blueLightActive ? 'ACTIVATED' : 'STANDBY',
      forward_surgical: blueLightActive ? 'DEPLOY FORWARD' : 'STANDARD POSITION'
    },
    
    // Recommendations based on ACTUAL status
    recommendations: blueLightActive ? [
      'Monitor surge capacity effectiveness',
      'Track extended shift fatigue levels',
      'Ensure blood product resupply pipeline',
      'Coordinate additional MEDEVAC assets',
      'Plan for protocol deactivation when conditions improve'
    ] : blueLightRecommended && !blueLightDismissed ? [
      'Command decision required on Blue Light Protocol activation',
      'Review operational constraints (security, resources, tempo)',
      'Coordinate with higher HQ if activation approved',
      'Document decision rationale for after-action review'
    ] : blueLightDismissed ? [
      'Continue monitoring medical system capacity',
      'Re-assess if operational constraints change',
      'Maintain contingency plans for surge capacity',
      'Document ongoing justification for non-activation'
    ] : [
      'Maintain current medical posture',
      'Continue routine monitoring',
      'Ensure surge capacity plans are updated'
    ]
  };
}

// ============================================================================
// 10. MEDEVAC SUMMARY FORMATTER - Evacuation Status
// ============================================================================

export function formatMEDEVACSummary(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const medConfig = scenario.medical_configuration || {};
  const corps = calc.by_corps || {};
  const medAnalysis = calc.medical_system_analysis || {};
  
  
  // BUG-T47 fix: read actual helicopter data from medevac calc, not hardcoded values
  const heliCalc = medAnalysis.medevac?.helicopter || {};
  const aviationAssets = medConfig.aviation_assets || medConfig.medevac_assets || [];
  const totalHelicopters = heliCalc.total_aircraft
    || (Array.isArray(aviationAssets) && aviationAssets.length > 0 ? aviationAssets.length : 0)
    || medConfig.medevac?.helicopters || 0;
  const available = heliCalc.available_aircraft
    || medConfig.medevac?.available
    || (totalHelicopters > 0 ? Math.round(totalHelicopters * 0.9) : 0);
  const availRate = heliCalc.effective_availability_percent || safePercentage(available, totalHelicopters);
  const dailyMissions = heliCalc.daily_sorties
    || Math.round((corps.total_wia || 0) / (calc.duration_days || 1) / 4);

  return {
    metadata: {
      report_type: 'MEDEVAC_SUMMARY',
      dtg: formatDateTime(new Date()),
      reporting_unit: scenario.force?.designation || 'Unknown Unit',
      classification: 'NATO UNCLASSIFIED'
    },

    air_assets: {
      total: totalHelicopters,
      available: available,
      availability_rate: availRate,
      status: available >= totalHelicopters * 0.5 ? 'GREEN' : 'AMBER',
      missions_last_24h: dailyMissions,
      golden_hour_achievement: medAnalysis.overall_golden_hour_percent ?? null,
      // BUG-6K-002: null when missions = 0 — undefined average is not 35 min
      avg_response_time: dailyMissions > 0 ? (medAnalysis.avg_response_time || null) : null,

      helicopters: {
        total: totalHelicopters,
        available: available,
        on_mission: Math.min(dailyMissions, available),
        maintenance: Math.max(0, totalHelicopters - available)
      },
      
      ground_ambulances: {
        total: 12,
        available: 10,
        on_mission: 2,
        maintenance: 0,
        status: 'GREEN'
      },
      
      routes: [
        { name: 'ROUTE BLUE', from: 'Role 1', to: 'Role 2', distance: 25, status: 'OPEN' },
        { name: 'ROUTE RED', from: 'Role 2', to: 'Role 3', distance: 75, status: 'OPEN' }
      ]
    },
    
    
    medevac_missions: {
      total: Math.round((corps.total_wia || 0) / (calc.duration_days || 1)),
      by_medevac: Math.round((corps.total_wia || 0) / (calc.duration_days || 1)),
      // BUG-6K-002: null when total missions = 0
      avg_response_time: Math.round((corps.total_wia || 0) / (calc.duration_days || 1)) > 0
        ? (medAnalysis.avg_response_time || null)
        : null,
      golden_hour_pct: medAnalysis.overall_golden_hour_percent || 78,
      declined_weather: 0,
      declined_threat: 0
    },

    evacuation_note: 'CASEVAC informational only per AJMedP-2 - not a planning factor (ARCH-010).'
  };
}

// ============================================================================
// 11. EXECUTIVE BRIEF FORMATTER - One-Page BLUF for Leadership
// T5-012: Added CASEVAC section
// ============================================================================

export function formatExecutiveBrief(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const corps = calc.by_corps || {};
  const medAnalysis = calc.medical_system_analysis || {};
  const medReq = calc.medical_requirements || {};
  
  
  const totalCas = (corps.total_all_sources || corps.total_casualties || 0) + (corps.total_dnbi || 0);
  // BUG-6K-003 FIX: null when no calculation results; 78 was hardcoded with no basis
  const goldenHour = medAnalysis.overall_golden_hour_percent ?? null;
  
  const overallStatus = goldenHour >= 80 ? 'ADEQUATE' : goldenHour >= 60 ? 'STRAINED' : 'CRITICAL';
  
  return {
    metadata: {
      report_type: 'EXECUTIVE_BRIEF',
      dtg: formatDateTime(new Date()),
      reporting_unit: scenario.force?.designation || 'Unknown Unit',
      classification: 'NATO UNCLASSIFIED'
    },
    
    executive_summary: {
      bottom_line_up_front: `Medical support is ${overallStatus} for planned operations. ` +
        `Projected ${totalCas} total casualties over ${calc.duration_days || 0} days. ` +
        `Golden hour achievement: ${goldenHour}%. ` +
        `${goldenHour < 80 ? 'Recommend additional MEDEVAC assets.' : 'No critical shortfalls identified.'}`,
      force_size: scenario.force?.total_personnel || calc.total_personnel || 0,
      total_casualties: totalCas
    },
    
    executive_brief: {
      bluf: `Medical support ${overallStatus}`,
      key_metrics: [
        { label: 'Total Casualties', value: totalCas, status: totalCas > 500 ? 'AMBER' : 'GREEN' },
        { label: 'KIA', value: corps.total_kia || 0, status: 'RED' },
        { label: 'WIA', value: corps.total_wia || 0, status: 'AMBER' },
        { label: 'Golden Hour', value: `${goldenHour}%`, status: goldenHour >= 80 ? 'GREEN' : 'AMBER' },
        { label: 'Blood Units', value: medReq.blood?.total_units || 0, status: 'GREEN' },

      ],
      recommendations: [
        ...(goldenHour < 80 ? [
          'Increase MEDEVAC helicopter availability',
          'Consider forward positioning of Role 2',
          'Pre-position blood products at Role 1'
        ] : []),
        ...(goldenHour >= 80 ? [
          'Maintain current medical posture',
          'Continue monitoring casualty rates'
        ] : [])
      ]
    },
    
    battle_casualties: {
      total: corps.total_all_sources || corps.total_casualties || 0,
      kia: corps.total_kia || 0,
      dow: corps.total_dow || 0,
      wia: corps.total_wia || 0,
      daily_rate: corps.avg_daily_rate || 0
    },
    
    dnbi: {
      total: corps.total_dnbi || 0,
      daily_rate: (corps.total_dnbi || 0) / (calc.duration_days || 1)
    },
    
    
    components: {
      facilities: { status: 'GREEN', assessment: 'Adequate bed capacity' },
      medevac: { status: goldenHour >= 80 ? 'GREEN' : 'AMBER', assessment: `${goldenHour}% golden hour` },
      medlog: { status: 'GREEN', assessment: '7 DOS Medical Materiel' },
      personnel: { status: 'GREEN', assessment: '95% staffed' },
      overall: { 
        status: overallStatus === 'ADEQUATE' ? 'GREEN' : 'AMBER', 
        assessment: overallStatus === 'ADEQUATE' ? 'Ready' : 'Monitor closely' 
      }
    },
    
    recommendations: [
      ...(goldenHour < 80 ? ['Request additional MEDEVAC assets'] : []),
      'Consider forward MTF positioning'
    ].filter(r => r),
    
    risks: [
      ...(goldenHour < 80 ? [{ risk: 'MEDEVAC capacity', severity: 'MEDIUM' }] : []),
    ]
  };
}

// ============================================================================
// 12. NATO PLANNING FORMAT - Annex Q Medical Estimate (AJMedP-5)
// ============================================================================

export function formatNATOPlanningFormat(scenario) {
  const calc = scenario.calculation_results || scenario.casualty_projections || {};
  const force = scenario.force || {};
  const ops = scenario.operation_parameters || {};
  const corps = calc.by_corps || {};
  const medAnalysis = calc.medical_system_analysis || {};
  
  
  const totalDNBI = corps.total_dnbi || 0;
  const totalBattle = (corps.total_kia || 0) + (corps.total_dow || 0) + (corps.total_wia || 0);
  // BUG-T52 fix: total_all_sources / total_casualties may already include DNBI — use explicit battle sum.
  // BUG-6K-003 FIX: null when no calculation results; 78 was hardcoded with no basis
  const goldenHour = medAnalysis.overall_golden_hour_percent ?? null;

  // Determine overall status considering CASEVAC
  const overallStatus = goldenHour >= 80 ? 'GREEN' : goldenHour >= 60 ? 'AMBER' : 'RED';

  return {
    metadata: {
      report_type: 'NATO_PLANNING',
      dtg: formatDateTime(new Date()),
      reporting_unit: force.designation || 'FORCE',
      reporting_period: `${calc.duration_days || ops.duration_days || 0} days`,
      classification: 'NATO UNCLASSIFIED',
      stanag: 'AJMedP-5'
    },

    executive_summary: {
      force_size: force.total_personnel || calc.total_personnel || 0,
      // BUG-T52 fix: battle (KIA+DOW+WIA) + DNBI = all non-RTD losses; no double-count
      total_casualties: totalBattle + totalDNBI
    },
    
    battle_casualties: {
      // BUG-T50 fix: battle = KIA+DOW+WIA only (applied across all formatters)
      total: (corps.total_kia || 0) + (corps.total_dow || 0) + (corps.total_wia || 0),
      kia: corps.total_kia || 0,
      wia: corps.total_wia || 0,
      dow: corps.total_dow || 0,
      // BUG-T52 fix: guard against division by zero; use explicit formula not avg_daily_rate which may be 0
      daily_rate: (force.total_personnel || 0) > 0
        ? Math.round(((corps.total_kia || 0) + (corps.total_dow || 0) + (corps.total_wia || 0))
            / (force.total_personnel || 1) * 1000 / (calc.duration_days || 1) * 10) / 10
        : null
    },

    dnbi: {
      total: totalDNBI,
      // BUG-T52 fix: rate per 1000 per day (not total rate); guarded denominator
      daily_rate: Math.round(totalDNBI / (calc.duration_days || 1) * 10) / 10,
      rate_per_1000_per_day: (force.total_personnel || 0) > 0
        ? Math.round(totalDNBI / (force.total_personnel || 1) * 1000 / (calc.duration_days || 1) * 10) / 10
        : null
    },
    
    evacuation: {
      paragraph_title: 'Patient Evacuation',
      medevac: {
        capacity: 'MEDEVAC helicopters provide primary evacuation',
        golden_hour: `${goldenHour}% achievement rate`,
        status: goldenHour >= 80 ? 'ADEQUATE' : 'REQUIRES REINFORCEMENT'
      },
      casevac_note: 'CASEVAC informational only per AJMedP-2 - not a planning factor (ARCH-010).',
      summary: goldenHour >= 80
        ? 'Evacuation capacity adequate.'
        : 'MEDEVAC capacity below target - additional assets required.'
    },
    
    components: {
      facilities: {
        status: 'GREEN',
        assessment: 'MTF capacity adequate for projected patient load'
      },
      medevac: {
        status: goldenHour >= 80 ? 'GREEN' : goldenHour >= 60 ? 'AMBER' : 'RED',
        assessment: `${goldenHour}% golden hour achievement projected`
      },

      medlog: {
        status: 'GREEN',
        assessment: 'Medical Materiel and blood products sufficient for operation'
      },
      personnel: {
        status: 'GREEN',
        assessment: 'Medical personnel fill rates adequate'
      },
      overall: {
        status: overallStatus,
        assessment: overallStatus === 'GREEN' 
          ? 'Medical support is adequate for planned operations'
          : overallStatus === 'AMBER'
            ? 'Medical support requires reinforcement for optimal outcomes'
            : 'Medical support critical - immediate action required'
      }
    },
    
    risks: [
      ...(goldenHour < 80 ? [
        { risk: 'Evacuation timeline', mitigation: 'Request additional MEDEVAC' }
      ] : []),

      ...(goldenHour < 60 ? [
        { risk: 'Surge capacity', mitigation: 'Pre-position forward surgical team' }
      ] : [])
    ],

  };
}

// ============================================================================
// DEFAULT EXPORT - All 12 Formatters
// ============================================================================

const reportFormattersModule = {
  // All 12 Report Formatters
  formatMEDSITREP,
  formatCasualtyEstimate,
  formatComponentsMatrix,
  formatMEDASSESSREP,
  formatEPINATO2,
  formatNineLine,
  formatMETHANE,
  formatOOSR,
  formatBlueLightProtocol,
  formatMEDEVACSummary,
  formatExecutiveBrief,
  formatNATOPlanningFormat,
  
  // Helper Functions
  determineStatus,
  formatNumber,
  safePercentage,
  formatDateTime,
};

export default reportFormattersModule;
